import React from 'react'
import {
  Card,
  CardBody,
  CardTitle,
  CardText,
  Badge,
  Button,
  FormGroup,
  Label,
  Row,
  Col,
  Media,
  CustomInput,
  Input,
  FormFeedback,
  FormText,
  Form
} from 'reactstrap'
import { firstLetter } from 'common'

import Slider from '@mui/material/Slider'
import { CopyToClipboard } from 'react-copy-to-clipboard'

import * as yup from 'yup'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import moment from 'moment'
import { PromosApi } from 'typescript-axios'
import toast from 'react-hot-toast'
import classNames from 'classnames'
import * as enums from '../utils/enums'
import PremiumModal from './PremiumModal'
import AsyncForm from './AsyncForm'
import LoadingWidget from './LoadingWidget'
import { UserContext } from '../contexts/UserContext'
import DeleteModal from './DeleteModal'
import { getAxiosParams } from '../utils/client'
import GuruModal from './GuruModal'
import useTranslation from '../hooks/UseTranslation'

const PROMO_SERVICE = new PromosApi(getAxiosParams())

const PromoCard = ({
  promo,
  onDelete,
  onEdit,
  key,
  hasAccess,
  togglePremium
}) => {
  const { t } = useTranslation()

  const listingTypeStr = t(
    {
      online: 'online',
      physical: 'physical',
      all: 'pack_type_all'
    }[promo.listing_type]
  )

  const listingTypeFlag = enums.LISTING_TYPE_STR_TO_FLAG[promo.listing_type]
  const isActive = !!hasAccess && promo.is_active

  return (
    <Card className='h-100 border-0 shadow' key={key}>
      <CardBody className='d-flex flex-column justify-content-between'>
        <CardTitle className='mb-2 dashboard-title' tag='h6'>
          {promo.code}
        </CardTitle>
        <CardText className='text-muted d-flex flex-column justify-content-between h-100'>
          <div>
            <span className='text-black font-weight-bold'>
              {t(enums.PROMO_TYPE_FLAG_TO_STR[promo.promo_type])}
            </span>
          </div>
          <div>
            <span>
              {promo.expiry
                ? t(
                    'promo_valid_until',
                    moment(promo.expiry).format('DD/MM/YY')
                  )
                : t('promo_no_expiry')}
            </span>
          </div>
          <div>
            <span>
              {promo.is_unique ? t('promo_unique') : t('promo_not_unique')}
            </span>
          </div>
          {/* <div>
                    {
                        firstLetter(t(
                            {
                                1: "promo_n_templates_singular",
                                0: "promo_n_templates_all",
                            }[promo.listing_templates?.length] ?? "promo_n_templates_plural",
                            promo.listing_templates?.length
                        ))
                    }
                </div> */}
          <div>
            {/* can't change to x & LISTING_TYPE_ONLINE && <i>... 0 appears if falsse */}
            {listingTypeFlag & enums.LISTING_TYPE_ONLINE ? (
              <i className='fa fa-globe ml-1' />
            ) : (
              ''
            )}
            {listingTypeFlag & enums.LISTING_TYPE_PHYSICAL ? (
              <i className='fa fa-home ml-1' />
            ) : (
              ''
            )}
          </div>
          <div className='d-flex align-items-center justify-content-between mt-2'>
            <div>
              <Badge
                className='mb-2'
                size='sm'
                color={isActive ? 'info' : 'light'}
              >
                {t(isActive ? 'enabled' : 'disabled')}
              </Badge>
            </div>
            <div className='h5 text-primary text-right'>
              {t('percent_str', promo.discount)}
            </div>
          </div>
        </CardText>
        <CopyToClipboard
          text={promo.code}
          onCopy={() => {
            toast.success(t('copied'))
          }}
        >
          <Button disabled={!hasAccess} size='sm' color='primary'>
            {t('copy')}
          </Button>
        </CopyToClipboard>
      </CardBody>
      <div className={classNames('close-absolute pt-1')}>
        {!!onEdit && (
          <i
            className='fa fa-pen pointer text-muted'
            onClick={(e) => {
              e.stopPropagation()
              if (hasAccess) {
                onEdit(promo)
              } else {
                togglePremium()
              }
            }}
          />
        )}
        {!!onDelete && (
          <i
            className='fa fa-trash fa-fw mx-2 pointer text-error'
            onClick={(e) => {
              e.stopPropagation()
              if (hasAccess) {
                onDelete(promo)
              } else {
                togglePremium()
              }
            }}
          />
        )}
      </div>
    </Card>
  )
}

const PromoForm = ({ initial, listingTemplates, onSubmit, error }) => {
  const { t } = useTranslation()
  const [isDateOpen, setIsDateOpen] = React.useState(false)
  const dateTimeRef = React.useRef()

  let schema = yup.object().shape({
    code: yup.string().min(3).max(8),
    promo_type: yup.number().min(1),
    discount: yup.number().min(5).max(90).required(),
    isReusable: yup.bool().required(),
    expiry: yup.date().nullable().default(null),
    // listing_templates: yup.array().nullable().default(null),
    listing_type: yup.number().min(1)
  })
  if (initial) {
    schema = schema.concat(
      yup.object().shape({
        is_active: yup.bool()
      })
    )
  }

  // const templateOpts = []
  // listingTemplates.map(t => templateOpts.push({ label: t.title, value: t }))

  let initialValues = {
    code: '',
    discount: 50,
    promo_type: enums.PROMO_TYPE_ALL,
    isReusable: false,
    expiry: null,
    // listing_templates: null,
    listing_type: enums.LISTING_TYPE_ALL
  }

  if (initial) {
    initialValues = {
      code: initial.code,
      discount: initial.discount,
      is_active: initial.is_active,
      isReusable: !initial.is_unique,
      promo_type: initial.promo_type,
      listing_type: enums.LISTING_TYPE_STR_TO_FLAG[initial.listing_type],
      expiry: initial.expiry ? moment(initial.expiry) : null
    }
    // if (!!initial.listing_templates && initial.listing_templates.length > 0) {
    //     initialValues.listing_templates = templateOpts.filter(
    //         opt => initial.listing_templates.findIndex(t => t.id == opt.value.id) !== -1
    //     )
    // }
  }

  return (
    <AsyncForm
      initialValues={initialValues}
      validationSchema={schema}
      enableReinitialize
      onSubmit={(values) => {
        const outValues = {
          code: values.code,
          discount: values.discount,
          is_unique: !values.isReusable,
          expiry: values.expiry?.endOf('day'),
          promo_type: values.promo_type,
          listing_type: enums.LISTING_TYPE_FLAG_TO_STR[values.listing_type]
        }
        // const templatesIds = []
        // values.listing_templates?.map(({ value }) => templatesIds.push(value.id))
        // outValues.listing_template_ids = templatesIds
        if (initial) {
          outValues.is_active = values.is_active
        }
        return onSubmit(outValues)
      }}
    >
      {(isLoading, { errors, values, handleChange, handleSubmit, isValid }) => {
        const renderInput = ({
          ref,
          inputProps,
          disabled,
          onChange,
          ...other
        }) => {
          const date = inputProps.value
            ? moment(inputProps.value, 'DD/MM/YY')
                .local()
                .format('ddd D MMM YY')
            : t('promo_no_expiry')
          return (
            <div ref={ref} {...other}>
              <div className='d-flex align-items-center'>
                <Input
                  {...inputProps}
                  placeholder={t('datepicker_placeholder')}
                  className='form-control flex-grow pointer'
                  value={date}
                  disabled={disabled}
                  onChange={onChange}
                  onClick={() => setIsDateOpen((open) => !open)}
                />
                <Button
                  color='link'
                  className='text-danger'
                  onClick={() =>
                    handleChange({ target: { name: 'expiry', value: null } })
                  }
                >
                  <i className='fas fa-times fa-fw fa-lg' />
                </Button>
              </div>
            </div>
          )
        }

        return (
          <Form noValidate onSubmit={handleSubmit}>
            {!!initial && (
              <FormGroup>
                <CustomInput
                  type='switch'
                  id='is_active'
                  name='is_active'
                  className='mb-2'
                  onChange={(e) =>
                    handleChange({
                      target: { name: 'is_active', value: !!e.target.checked }
                    })
                  }
                  checked={!!values.is_active}
                  label={
                    <strong className='text-sm'>
                      {values.is_active ? t('enabled') : t('disabled')}
                    </strong>
                  }
                />
              </FormGroup>
            )}
            <FormGroup>
              <Label className='form-label' for='code'>
                {t('promo_code_label')}
              </Label>
              <Input
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: 'code',
                      value: e.target.value.toUpperCase()
                    }
                  })
                }
                value={values.code}
                invalid={!!errors.code}
                name='code'
                className='form-control font-weight-bold'
                placeholder={t('promo_code_placeholder')}
              />
              <FormFeedback>{errors.code}</FormFeedback>
            </FormGroup>
            <div>
              <Label className='form-label'>{t('promo_type_label')}</Label>
            </div>
            <FormGroup check inline>
              <CustomInput
                type='checkbox'
                id='promoTypeListing'
                className='mb-2'
                invalid={!!errors.promo_type}
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: 'promo_type',
                      value: e.target.checked
                        ? values.promo_type | enums.PROMO_TYPE_LISTING
                        : values.promo_type & ~enums.PROMO_TYPE_LISTING
                    }
                  })
                }
                checked={values.promo_type & enums.PROMO_TYPE_LISTING}
                label={
                  <strong className='text-sm'>{t('promo_type_listing')}</strong>
                }
              />
            </FormGroup>
            <FormGroup check inline>
              <CustomInput
                type='checkbox'
                id='promoTypePack'
                className='mb-2'
                invalid={!!errors.promo_type}
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: 'promo_type',
                      value: e.target.checked
                        ? values.promo_type | enums.PROMO_TYPE_PACK
                        : values.promo_type & ~enums.PROMO_TYPE_PACK
                    }
                  })
                }
                checked={values.promo_type & enums.PROMO_TYPE_PACK}
                label={
                  <strong className='text-sm'>{t('promo_type_pack')}</strong>
                }
              />
            </FormGroup>
            <div>
              <Label className='form-label'>{t('promo_templates_label')}</Label>
            </div>
            <FormGroup check inline>
              <CustomInput
                type='checkbox'
                id='isOnline'
                name='isOnline'
                className='mb-2'
                invalid={!!errors.listing_type}
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: 'listing_type',
                      value: e.target.checked
                        ? values.listing_type | enums.LISTING_TYPE_ONLINE
                        : values.listing_type & ~enums.LISTING_TYPE_ONLINE
                    }
                  })
                }
                checked={values.listing_type & enums.LISTING_TYPE_ONLINE}
                label={
                  <strong className='text-sm'>
                    {firstLetter(t('online'))}
                  </strong>
                }
              />
            </FormGroup>
            <FormGroup check inline>
              <CustomInput
                type='checkbox'
                id='isPhysical'
                name='isPhysical'
                className='mb-2'
                invalid={!!errors.listing_type}
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: 'listing_type',
                      value: e.target.checked
                        ? values.listing_type | enums.LISTING_TYPE_PHYSICAL
                        : values.listing_type & ~enums.LISTING_TYPE_PHYSICAL
                    }
                  })
                }
                checked={values.listing_type & enums.LISTING_TYPE_PHYSICAL}
                label={
                  <strong className='text-sm'>
                    {firstLetter(t('physical'))}
                  </strong>
                }
              />
            </FormGroup>
            <FormGroup>
              <div className='d-flex justify-content-between'>
                <Label className='form-label' for='discount'>
                  {t('discount')}
                </Label>
                <strong>{t('percent_str', values.discount)}</strong>
              </div>
              <Slider
                color='primary'
                aria-label='discount'
                valueLabelDisplay='auto'
                step={5}
                onChange={(e, newVal) =>
                  handleChange({ target: { name: 'discount', value: newVal } })
                }
                value={values.discount}
                min={5}
                max={90}
              />
              <div className='nouislider-values'>
                <div className='min'>
                  {/* fixme hardcoded currency */}
                  <span id='slider-snap-value-from'>{t('percent_str', 5)}</span>
                </div>
                <div className='max'>
                  {/* fixme hardcoded currency */}
                  <span id='slider-snap-value-to'>{t('percent_str', 90)}</span>
                </div>
              </div>
            </FormGroup>
            <FormGroup>
              <div ref={dateTimeRef} />
              <Label className='form-label' for='expiry'>
                {t('promo_expiry')}
              </Label>
              <DatePicker
                disableClearable={false}
                open={isDateOpen}
                onOpen={() => setIsDateOpen(true)}
                onClose={() => setIsDateOpen(false)}
                minDate={moment()}
                renderInput={renderInput}
                label={t('promo_expiry')}
                value={values.expiry}
                onChange={(newValue) =>
                  handleChange({ target: { name: 'expiry', value: newValue } })
                }
                PopperProps={{
                  placement: 'bottom-end',
                  anchorEl: dateTimeRef.current
                }}
              />
              <FormText>{t('promo_expiry_explained')}</FormText>
            </FormGroup>
            <FormGroup>
              <Label className='form-label'>{t('promo_unique_label')}</Label>
              <CustomInput
                type='switch'
                id='isReusable'
                name='isReusable'
                className='mb-2'
                onChange={(e) =>
                  handleChange({
                    target: { name: 'isReusable', value: !!e.target.checked }
                  })
                }
                checked={!!values.isReusable}
                label={
                  <strong className='text-sm'>
                    {values.isReusable ? t('yes') : t('no')}
                  </strong>
                }
              />
              <FormText>{t('promo_unique_explained')}</FormText>
            </FormGroup>
            {/* <FormGroup className="mb-4"> */}
            {/* <Select
                                name="listing_templates"
                                inputId="form_templates"
                                options={templateOpts}
                                placeholder={firstLetter(t("promo_no_templates"))}
                                isSearchable={false}
                                isClearable={true}
                                isMulti={true}
                                className="form-control dropdown bootstrap-select"
                                classNamePrefix="selectpicker"
                                onChange={value => handleChange({ target: { name: "listing_templates", value } })}
                                value={values.listing_templates}
                            /> */}
            {/* </FormGroup> */}
            <div className='text-right mt-2'>
              <Button
                type='submit'
                color='primary'
                disabled={!isValid || isLoading}
              >
                {initial ? t('update') : t('add')}
              </Button>
            </div>
          </Form>
        )
      }}
    </AsyncForm>
  )
}

const PromoCodes = ({ listingTemplates }) => {
  const { t } = useTranslation()
  const { loggedUser, setLoggedUser } = React.useContext(UserContext)
  const [promos, setPromos] = React.useState(null)
  const [promoToDelete, setPromoToDelete] = React.useState()
  const [promoToEdit, setPromoToEdit] = React.useState()
  const [nameForUpdate, setNameForUpdate] = React.useState(null)
  const [nameForCreate, setNameForCreate] = React.useState(null)
  const [showAddModal, setShowAddModal] = React.useState(false)
  const [showPremium, setShowPremium] = React.useState(false)
  const [createStatus, setCreateStatus] = React.useState({
    disabled: true,
    exists: false
  })
  const [updateStatus, setUpdateStatus] = React.useState({
    disabled: true,
    exists: false
  })
  const [error, setError] = React.useState()

  const hasAccess = !!loggedUser?.active_premium

  const toggleAdd = () => {
    if (hasAccess) {
      setShowAddModal((showAddModal) => !showAddModal)
    } else {
      setShowPremium((p) => !p)
    }
  }

  React.useEffect(() => {
    PROMO_SERVICE.readPromosMeApiV1PromosMeGet().then((res) =>
      setPromos(res.data)
    )
  }, [])

  React.useEffect(() => {
    if (promoToEdit) {
      setShowAddModal(true)
    }
  }, [promoToEdit])

  const onSubmit = (data) => {
    if (promoToEdit) {
      return PROMO_SERVICE.updatePromoApiV1PromosPromoIdPut(
        promoToEdit.id,
        data
      ).then((res) => {
        const indexOf = promos.findIndex((p) => p.id == res.data.id)
        if (indexOf > -1) {
          promos.splice(indexOf, 1, res.data)
        }
        setPromos([...promos])
        setShowAddModal(false)
      })
    }
    return PROMO_SERVICE.createPromoApiV1PromosPost(data)
      .then((res) => {
        setPromos([res.data, ...promos])
        setShowAddModal(false)
        setError(null)
      })
      .catch((e) => {
        if (e.response.data.detail) {
          console.debug(e.response.data.detail)
          setError(e.response.data.detail)
        } else {
          throw e
        }
      })
  }

  const onDelete = () =>
    PROMO_SERVICE.deletePromoApiV1PromosPromoIdDelete(promoToDelete.id).then(
      () => {
        const indexOf = promos.findIndex((p) => p.id == promoToDelete.id)
        if (indexOf > -1) {
          promos.splice(indexOf, 1)
        }
        setPromos([...promos])
        setPromoToDelete(null)
      }
    )

  return (
    <div className='text-block'>
      <PremiumModal
        isOpen={!!showPremium}
        toggle={() => setShowPremium(false)}
      />
      <DeleteModal
        isOpen={promoToDelete}
        toggle={() => setPromoToDelete(null)}
        title={t('promo_delete_modal_title', promoToDelete?.code)}
        delete={(e) => onDelete(e, promoToDelete)}
      />
      <GuruModal
        isOpen={showAddModal}
        toggle={toggleAdd}
        onClosed={() => {
          setPromoToEdit(null)
          setError(null)
        }}
        title={
          promoToEdit
            ? t('promo_edit_modal_title', promoToEdit?.code)
            : t('promo_add_modal_title')
        }
        hideFooter
      >
        <PromoForm
          error={error}
          initial={promoToEdit}
          listingTemplates={listingTemplates}
          onSubmit={onSubmit}
        />
      </GuruModal>
      <div className='d-flex justify-content-between'>
        <h5 className='text-first-letter mb-4'>{t('promo_title')}</h5>
        <h5>
          <Badge color='premium'>{t('premium')}</Badge>
        </h5>
      </div>
      <Media className='text-muted mb-4'>
        <Media body className='mt-n1'>
          <div dangerouslySetInnerHTML={{ __html: t('promo_content') }} />
        </Media>
      </Media>
      <div>
        {promos === null && <LoadingWidget />}
        {!!promos && promos.length == 0 && (
          <p className='text-muted mb-4'>{t('promo_no_promos')}</p>
        )}
        {!!promos && promos.length > 0 && (
          <Row>
            {promos.map((promo) => (
              <Col key={`promo${promo.id}`} xs='6' md='4' className='mb-5'>
                <PromoCard
                  promo={promo}
                  hasAccess={hasAccess}
                  togglePremium={() => setShowPremium((p) => !p)}
                  onDelete={setPromoToDelete}
                  onEdit={setPromoToEdit}
                />
              </Col>
            ))}
          </Row>
        )}
        {promos !== null && (
          <Button color='outline-primary' onClick={toggleAdd}>
            <i className='fa fa-plus fa-fw mr-2 text-right' />
            {t('promo_create_cta')}
          </Button>
        )}
      </div>
    </div>
  )
}

export default PromoCodes
